import { codecs } from "@ppb/tbd-urn-codecs";
import { createSelector } from "reselect";
import { ApplicationState } from "@ppb/tbd-store/state/ApplicationState.types";
import { BetslipSportsbookReport } from "@ppb/tbd-store";
import { formatOdds } from "@ppb/formatters";
import {
  BettingMetadata,
  RunnersMetadata,
  SportsbookBettingRunnerData,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";
import { PlaceResult, LEG_TYPES } from "@ppb/betslip-core";
import { BetSegmentsIcons } from "../BetSegments/BetSegments.types";
import {
  isCast,
  isMultiple,
  isSingle,
  isBetBuilder,
  isMultiBetBuilder,
  isBoostedMultiple,
  isLotteries,
  getLotteryPickKey,
} from "@ppb/tbd-store/helpers/sportsbook-betting";
import { Entities } from "@ppb/tbd-store/state/entities/Entities.types";
import { createSportEventByURNSelector } from "@ppb/tbd-store/state/entities/sport-events/sport-event-selectors";
import { createRaceByURNSelector } from "@ppb/tbd-store/state/entities/races/race-selectors";
import {
  getHasGenerosityWallets,
  getSportsbookReport,
} from "../../store/state/betslip-module/betslip-module-selectors";
import { legHasBonusBall } from "../../helpers/bonus-ball";
import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { currencyFormatWithDecimalPlaces } from "../../formatters/currency-adapter";
import { i18n } from "../../helpers/i18n";
import {
  buildSportsbookFreeBetsLabel,
  buildOriginalPotentialReturns,
  buildPotentialReturns,
  getCastOrdinal,
  buildRacingTitle,
  buildOdds,
} from "../betslip-formatters";
import { toOdds, Odds } from "../../formatters/odds-adapter";
import { buildMultiBetBuilderGroup, buildSelection } from "../connected-sportsbook-betslip-mapper";
import type { BetslipLocalization, BetslipOddsDisplay, SportsbookBetslipCapabilities } from "../../contract";

import { getBoostedInfo } from "../../helpers/boosted-info";
import { getSelectionTypeIcon } from "../../helpers/selection-type";
import { buildFreeBetsAlertMessage, getGenerosityReceiptAlertData } from "../../helpers/generosity-wallets";

import {
  SportsbookReceiptPanelBetBuilder,
  SportsbookReceiptPanelCastBet,
  SportsbookReceiptPanelMultiBetBuilder,
  SportsbookReceiptPanelMultiBetBuilderGroups,
  SportsbookReceiptPanelMultiple,
  SportsbookReceiptPanelOneLegBet,
  SportsbookReceiptPanelProps,
  SportsbookReceiptPanelSelection,
  SportsbookReceiptPanelSingle,
  CastBetRunnerSelectionProps,
} from "./snowflakes/SportsbookReceiptPanel/SportsbookReceiptPanel.types";
import { BetSelection } from "./snowflakes/SportsbookReceiptPanel/snowflakes/BetSelections/BetSelections.types";

const NO_SUPPORTED_SPORT_IDS: number[] = [];
const NO_UNSUPPORTED_EVENT_IDS: string[] = [];

type ReceiptBuilderProps = {
  localization: BetslipLocalization;
  capabilities: SportsbookBetslipCapabilities;
  oddsDisplay: BetslipOddsDisplay;
};

type Runner = {
  runnerURN: string;
  selectionId: number;
  name: string;
  resultType: string | null;
};

function mapBoostedLeg(
  leg: PlaceResult.PlacedLeg,
  report: BetslipSportsbookReport,
  localization: BetslipLocalization,
  oddsDisplay: BetslipOddsDisplay,
) {
  const {
    result: { relations },
    metadata,
  } = report;
  const { runners, id: legId } = leg;
  const [legRunnerId] = runners;
  const runnerRelations = relations.legsRunners[legId][legRunnerId];

  const { title, subtitle, icon, silkFallbackType } = buildSelection(metadata[legRunnerId], localization, {
    handicap: runnerRelations.handicap,
  });

  const odd = !leg.isBoosted ? buildOdds(leg.originalDisplayOdds || leg.displayOdds, oddsDisplay) : undefined;

  return {
    urn: metadata[legRunnerId].runnerUrn,
    id: leg.id,
    title,
    subtitle,
    icon,
    silkFallbackType,
    odd,
    is90Min: metadata[legRunnerId].is90Min,
  };
}

export const createSelectionsBuilder = () => {
  const getSportEventByURN = createSportEventByURNSelector();
  const getRaceByURN = createRaceByURNSelector();

  return createSelector(
    [
      getSportsbookReport,
      (_state: ApplicationState, { oddsDisplay }: ReceiptBuilderProps) => oddsDisplay,
      (_state: ApplicationState, { localization }: ReceiptBuilderProps) => localization,
      (_state: ApplicationState, { capabilities }: ReceiptBuilderProps) =>
        capabilities.receiptPushNotifications.enabled
          ? capabilities.receiptPushNotifications.unsupportedEventIds
          : NO_UNSUPPORTED_EVENT_IDS,
      (state: ApplicationState) => state.entities,
      (_state: ApplicationState, { capabilities }: ReceiptBuilderProps) =>
        capabilities.receiptPushNotifications.enabled
          ? capabilities.receiptPushNotifications.supportedSportIds
          : NO_SUPPORTED_SPORT_IDS,
      (_state: ApplicationState, { capabilities }: ReceiptBuilderProps) => capabilities.selectionTypeIcon.enabled,
    ],
    (
      report,
      oddsDisplay,
      localization,
      unsupportedEventIds,
      entities,
      supportedSportIds,
      isSelectionTypeIconEnabled,
    ) => {
      if (!report) {
        return [];
      }

      const {
        result: { legs, relations },
        metadata,
      } = report;
      return Object.values(legs)
        .filter((leg) => leg.legType === LEG_TYPES.SIMPLE_SELECTION)
        .map<SportsbookReceiptPanelSelection>((leg) => {
          const { runners, id: legId } = leg;
          const [legRunnerId] = runners;
          const runnerRelations = relations.legsRunners[legId][legRunnerId];

          const { title, subtitle, eventUrn, icon, trap, meetingCountry, silkFallbackType, racingSport } =
            buildSelection(metadata[legRunnerId], localization, {
              handicap: runnerRelations.handicap,
            });

          const isRacingType = metadata[legRunnerId].type === "RACING";

          const eventId = isRacingType
            ? getRaceByURN(entities.races, eventUrn)?.raceId
            : getSportEventByURN(entities.sportevents, eventUrn)?.eventId?.toString();

          // checks if the current sportId supports push notifications
          const isPushNotificationsSupported = supportedSportIds.includes(metadata[legRunnerId].sportId);

          // checks if all selections has the same sportId
          const isSameSportName =
            new Set(Object.values(metadata).map((betMetadata) => betMetadata.sportName)).size === 1;

          // checks if all selections events are unsupported
          const areAllUnsupported = Object.values(metadata).every(
            (betMetadata) => !supportedSportIds.includes(betMetadata.sportId),
          );

          // checks if all conditions are true, otherwise push notifications are unavailable for this selection
          const isPushNotificationsUnavailable =
            !areAllUnsupported &&
            (!eventId || unsupportedEventIds.includes(eventId)) &&
            (!isSameSportName || isPushNotificationsSupported);

          return {
            urn: metadata[legRunnerId].runnerUrn,
            id: leg.id,
            title,
            subtitle,
            icon,
            trap,
            meetingCountry,
            silkFallbackType,
            racingSport,
            action: i18n({ key: "I18N.BETSLIP.REMOVE" }),
            odd: buildOdds(leg.originalDisplayOdds || leg.displayOdds, oddsDisplay, report.result.isAnyPriceBoostUsed),
            is90Min: metadata[legRunnerId].is90Min,
            selectionTypeIcon: isSelectionTypeIconEnabled
              ? getSelectionTypeIcon(metadata[legRunnerId].marketType, metadata[legRunnerId].isSuperSub)
              : undefined,
            isPushNotificationsUnavailable,
          };
        });
    },
  );
};

const buildMultiple = (
  combination: PlaceResult.PlacedCombination,
  oddsDisplay: BetslipOddsDisplay,
  localization: BetslipLocalization,
  isFreeBetsSelected: boolean,
  isFreeBetsWalletsActive: boolean,
): SportsbookReceiptPanelMultiple => {
  const {
    betType,
    displayOdds,
    totalStake,
    totalPotentialReturns,
    originalTotalPotentialReturns,
    hasBonusUsed,
    totalBonusUsed,
    lines,
    isEachWaySelected,
    isAccaInsured,
    isAccaInsuredToken,
    betReceiptId,
    regulatorId,
    isPriceBoosted,
    originalDisplayOdds,
  } = combination;

  const key = `I18N.BETSLIP.SBK.MULTIPLE.${betType}_LINES` as const;
  let previousValue;
  let previousOdds;

  if (isPriceBoosted && originalTotalPotentialReturns && originalDisplayOdds) {
    previousValue = buildOriginalPotentialReturns(totalStake, originalTotalPotentialReturns, localization);
    previousOdds = formatOdds(toOdds(originalDisplayOdds), oddsDisplay);
  }

  const freeBetsLabel =
    !isFreeBetsWalletsActive && isFreeBetsSelected
      ? buildSportsbookFreeBetsLabel(totalBonusUsed, localization, "I18N.BETSLIP.USED_BONUS")
      : undefined;

  const { message: generosityAlertMessage, icon: generosityIconName } = getGenerosityReceiptAlertData({
    combination,
    localization,
    isFreeBetsWalletsActive,
  });

  return {
    title: i18n({ key }),
    odds: buildOdds(displayOdds, oddsDisplay),
    previousOdds,
    stake: currencyFormatWithDecimalPlaces({
      ...localization,
      value: totalStake,
    }),
    returns: buildPotentialReturns(totalStake, totalPotentialReturns, localization),
    previousValue,
    hasMyOddsBoost: isPriceBoosted,
    hasBonusUsed: hasBonusUsed && !isFreeBetsWalletsActive,
    freeBetsLabel,
    generosityAlertMessage,
    generosityIconName,
    lines,
    hasEachWay: isEachWaySelected,
    hasAccaInsurance: isAccaInsured && !isAccaInsuredToken,
    betReceiptId,
    regulatorId,
    betId: String(combination.betId),
  };
};

export const createMultiplesBuilder = () => {
  return createSelector(
    [
      getSportsbookReport,
      (_state: ApplicationState, { oddsDisplay }: ReceiptBuilderProps) => oddsDisplay,
      (_state: ApplicationState, { localization }: ReceiptBuilderProps) => localization,
      (state: BetslipApplicationState) => !!getHasGenerosityWallets(state),
    ],
    (report, oddsDisplay, localization, hasGenerosityWallets) => {
      if (!report) {
        return [];
      }

      const {
        result: { combinations },
        isFreeBetsSelected,
      } = report;

      return Object.values(combinations)
        .filter((combination) => isMultiple(combination))
        .sort((combinationA, combinationB) => combinationA.lines - combinationB.lines)
        .map((combination) =>
          buildMultiple(combination, oddsDisplay, localization, isFreeBetsSelected, hasGenerosityWallets),
        );
    },
  );
};

const buildCastSelections = (
  relations: PlaceResult.PlacedRunnerRelationMap,
  metadata: RunnersMetadata,
  localization: BetslipLocalization,
): CastBetRunnerSelectionProps[] =>
  Object.keys(relations)
    .sort((runnerIdA, runnerIdB) => {
      const relationA = relations[runnerIdA];
      const relationB = relations[runnerIdB];

      if (!relationA?.order || !relationB?.order) {
        return 0;
      }

      return relationA.order - relationB.order;
    })
    .map((runnerId) => {
      const relatedRunner = relations[runnerId];
      const runnerMetadata = metadata[runnerId];
      const selection = buildSelection(runnerMetadata, localization);

      return {
        id: runnerId,
        horse: selection.title,
        position: relatedRunner.order,
        positionOrdinal: relatedRunner.order ? getCastOrdinal(relatedRunner.order) : undefined,
        icon: selection.icon,
        meetingCountry: selection.meetingCountry,
        trap: selection.trap,
        racingSport: selection.racingSport,
        silkFallbackType: selection.silkFallbackType,
      };
    });

const buildCast = (
  result: PlaceResult.PlacedBetResults,
  combination: PlaceResult.PlacedCombination,
  metadata: RunnersMetadata,
  localization: BetslipLocalization,
  isFreeBetsWalletsActive: boolean,
): SportsbookReceiptPanelCastBet => {
  const { lines, totalStake, totalPotentialReturns, betReceiptId, regulatorId, hasBonusUsed, totalBonusUsed } =
    combination;
  const { legs, relations } = result;

  const combinationLeg = legs[combination.legs[0]];
  const combinationMetadata = metadata[combinationLeg.runners[0]];
  const legRunnersRelations = relations.legsRunners[combinationLeg.id];
  const title = buildRacingTitle(combinationMetadata, localization);

  const freeBetsLabel =
    !isFreeBetsWalletsActive && hasBonusUsed
      ? buildSportsbookFreeBetsLabel(totalBonusUsed, localization, "I18N.BETSLIP.USED_BONUS")
      : undefined;

  const { message: generosityAlertMessage, icon: generosityIconName } = getGenerosityReceiptAlertData({
    combination,
    localization,
    isFreeBetsWalletsActive,
  });

  return {
    title,
    selections: buildCastSelections(legRunnersRelations, metadata, localization),
    subtitle: i18n({ key: `I18N.BETSLIP.SBK.CAST.${combinationLeg.legType}` }),
    lines,
    stake: currencyFormatWithDecimalPlaces({
      ...localization,
      value: totalStake,
    }),
    returns: buildPotentialReturns(totalStake, totalPotentialReturns, localization),
    betReceiptId,
    regulatorId,
    betId: String(combination.betId),
    hasBonusUsed: hasBonusUsed && !isFreeBetsWalletsActive,
    freeBetsLabel,
    generosityAlertMessage,
    generosityIconName,
  };
};

export const createCastsBuilder = () => {
  return createSelector(
    [
      getSportsbookReport,
      (_state: ApplicationState, { localization }: ReceiptBuilderProps) => localization,
      (state: BetslipApplicationState) => !!getHasGenerosityWallets(state),
    ],
    (report, localization, hasGenerosityWallets) => {
      if (!report) {
        return [];
      }

      const { result, metadata } = report;
      const { combinations, legs } = result;

      return Object.values(combinations)
        .filter((combination) => isCast(combination, legs))
        .sort((combinationOne, combinationTwo) => {
          // Since all runners have the same parent race we just use one of the runners
          const combinationOneLegs = legs[combinationOne.legs[0]];
          const combinationTwoLegs = legs[combinationTwo.legs[0]];
          let combinationOneRacing;
          let combinationTwoRacing;
          const metadataOne = metadata[combinationOneLegs.runners[0]];
          const metadataTwo = metadata[combinationTwoLegs.runners[0]];

          if (metadataOne.type === "RACING") {
            combinationOneRacing = metadataOne.racing;
          }
          if (metadataTwo.type === "RACING") {
            combinationTwoRacing = metadataTwo.racing;
          }

          if (!combinationOneRacing || !combinationTwoRacing) {
            return 0;
          }

          const racingTimeDelta =
            new Date(combinationOneRacing.time).getTime() - new Date(combinationTwoRacing.time).getTime();

          if (racingTimeDelta !== 0) {
            return racingTimeDelta;
          }

          return combinationOne.lines - combinationTwo.lines;
        })
        .map((combination) => buildCast(result, combination, metadata, localization, hasGenerosityWallets));
    },
  );
};

const buildOneLineBet = (
  combination: PlaceResult.PlacedCombination,
  runners: PlaceResult.PlacedRunnersMap,
  runnerIds: string[],
  metadata: RunnersMetadata,
  oddsDisplay: BetslipOddsDisplay,
  localization: BetslipLocalization,
  isFreeBetsSelected: boolean,
  isFreeBetsWalletsActive: boolean,
  isSelectionTypeIconEnabled: boolean,
  hasBonusBall: boolean,
  drawCounter?: number,
): SportsbookReceiptPanelOneLegBet => {
  const { totalStake, totalPotentialReturns, totalBonusUsed, hasBonusUsed, betReceiptId, regulatorId } = combination;

  const metadataRunner: {
    name: string;
    subtitle: string;
    marketType: string;
    isSuperSub: boolean;
    runnerUrn: string;
    selectionId: number;
  }[] = [];

  runnerIds.forEach((runnerId) => {
    const runnerMetadata = metadata[runnerId];
    if (runnerMetadata?.runnerName) {
      const { subtitle } = buildSelection(runnerMetadata, localization, undefined, drawCounter);

      metadataRunner.push({
        name: runnerMetadata.runnerName,
        subtitle,
        marketType: runnerMetadata.marketType,
        isSuperSub: runnerMetadata.isSuperSub ?? false,
        runnerUrn: runnerMetadata.runnerUrn,
        selectionId: runners[runnerId].selectionId,
      });
    }
  });

  const lottoRunners: Runner[] = [];
  metadataRunner.forEach((currentMetadata) => {
    lottoRunners.push({
      runnerURN: currentMetadata.runnerUrn,
      selectionId: currentMetadata.selectionId,
      name: currentMetadata.name,
      resultType: null,
    });
  });

  const { displayOdds } = runners[runnerIds[0]];

  const formattedTotalStake = currencyFormatWithDecimalPlaces({
    ...localization,
    value: totalStake,
  });

  const freeBetsLabel =
    !isFreeBetsWalletsActive && isFreeBetsSelected
      ? buildSportsbookFreeBetsLabel(totalBonusUsed, localization, "I18N.BETSLIP.USED_BONUS")
      : undefined;

  const generosityAlertMessage =
    isFreeBetsWalletsActive && hasBonusUsed
      ? buildFreeBetsAlertMessage({ localization, combinationAmount: totalBonusUsed })
      : undefined;

  const { subtitle, marketType, isSuperSub } = metadataRunner[0];

  return {
    title: combination.id,
    subtitle,
    odds: buildOdds(displayOdds, oddsDisplay),
    profitOrLiability: buildPotentialReturns(totalStake, totalPotentialReturns, localization),
    stake: formattedTotalStake,
    hasBonusUsed: hasBonusUsed && !isFreeBetsWalletsActive,
    freeBetsLabel,
    generosityAlertMessage,
    selectionTypeIcon: isSelectionTypeIconEnabled ? getSelectionTypeIcon(marketType, isSuperSub) : undefined,
    runners: lottoRunners,
    hasBonusBall,
    betReceiptId,
    regulatorId,
    betId: String(combination.betId),
  };
};

export const createOneLineBetsBuilder = () => {
  return createSelector(
    [
      getSportsbookReport,
      (_state: ApplicationState, { oddsDisplay }: ReceiptBuilderProps) => oddsDisplay,
      (_state: ApplicationState, { localization }: ReceiptBuilderProps) => localization,
      (state: BetslipApplicationState) => !!getHasGenerosityWallets(state),
      (_state: ApplicationState, { capabilities }: ReceiptBuilderProps) => capabilities.selectionTypeIcon.enabled,
    ],
    (report, oddsDisplay, localization, hasGenerosityWallets, isSelectionTypeIconEnabled) => {
      if (!report) {
        return [];
      }

      const { result, metadata, isFreeBetsSelected } = report;
      const { combinations, legs, runners } = result;

      const grouped = Object.values(combinations)
        .filter((combination) => isLotteries(combination, legs))
        .reduce<Record<string, PlaceResult.PlacedCombination[]>>((groups, combination) => {
          const pick = getLotteryPickKey(legs[combination.legs[0]], metadata);

          if (!pick) return groups;

          if (!groups[pick.key]) {
            groups[pick.key] = [];
          }

          groups[pick.key].push(combination);

          return groups;
        }, {});

      return Object.values(grouped).map((groupCombinations) => {
        const [combination] = groupCombinations;
        const [legId] = combination.legs;
        const leg = legs[legId];

        const aggregatedCombination = groupCombinations.slice(1).reduce(
          (total, current) => ({
            ...total,
            totalStake: total.totalStake + current.totalStake,
            totalPotentialReturns:
              total.totalPotentialReturns == null || current.totalPotentialReturns == null
                ? null
                : total.totalPotentialReturns + current.totalPotentialReturns,
            totalBonusUsed: total.totalBonusUsed + current.totalBonusUsed,
            hasBonusUsed: total.hasBonusUsed || current.hasBonusUsed,
          }),
          { ...combination },
        );

        return buildOneLineBet(
          aggregatedCombination,
          runners,
          leg.runners,
          metadata,
          oddsDisplay,
          localization,
          isFreeBetsSelected,
          hasGenerosityWallets,
          isSelectionTypeIconEnabled,
          legHasBonusBall(leg),
          groupCombinations.length,
        );
      });
    },
  );
};

const buildSingle = (
  combination: PlaceResult.PlacedCombination,
  leg: PlaceResult.PlacedLeg,
  metadata: BettingMetadata,
  runnerRelations: SportsbookBettingRunnerData,
  oddsDisplay: BetslipOddsDisplay,
  localization: BetslipLocalization,
  unsupportedEventIds: string[],
  supportedSportIds: number[],
  isFreeBetsSelected: boolean,
  isFreeBetsWalletsActive: boolean,
  isSelectionTypeIconEnabled: boolean,
  isBestOddsGuaranteedLabelEnabled: boolean,
): SportsbookReceiptPanelSingle => {
  const {
    totalStake,
    totalPotentialReturns,
    totalBonusUsed,
    hasBonusUsed,
    isEachWaySelected: hasEachWay,
    eachWayPlacesFraction,
    eachWayPlaces,
    isGuaranteedPriceSelected,
    betReceiptId,
    regulatorId,
  } = combination;

  const { displayOdds } = leg;
  let previousOdds: Odds | null = metadata.previousOdds ? metadata.previousOdds[0] : null;

  let previousProfitOrLiability;
  if (combination.isPriceBoosted && combination.originalTotalPotentialReturns && combination.originalDisplayOdds) {
    previousProfitOrLiability = currencyFormatWithDecimalPlaces({
      ...localization,
      value: combination.originalTotalPotentialReturns,
    });
    previousOdds = toOdds(combination.originalDisplayOdds);
  }

  const formattedTotalStake = currencyFormatWithDecimalPlaces({
    ...localization,
    value: totalStake,
  });
  const { title, subtitle, icon, meetingCountry, silkFallbackType, racingSport, trap } = buildSelection(
    metadata,
    localization,
    runnerRelations,
  );

  const eachWaySubtitle = hasEachWay
    ? i18n({
        key: "I18N.BETSLIP.EACHWAY_ODDS_PLACES",
        interpolationValues: {
          numerator: eachWayPlacesFraction?.numerator || "",
          denominator: eachWayPlacesFraction?.denominator || "",
          places: eachWayPlaces || "",
        },
      })
    : "";

  const { isOddsboostMarketType, marketType } = metadata;

  let eventId;
  if (metadata.type === "RACING") {
    const { racing } = metadata;
    eventId = codecs.fixture.decode(racing.urn);
  } else {
    const { eventUrn } = metadata;
    eventId = codecs.fixture.decode(eventUrn);
  }

  const isPushNotificationsSupported = supportedSportIds.includes(metadata.sportId);

  const isPushNotificationsUnavailable =
    (!eventId || unsupportedEventIds.includes(eventId)) && isPushNotificationsSupported;

  const freeBetsLabel =
    !isFreeBetsWalletsActive && isFreeBetsSelected
      ? buildSportsbookFreeBetsLabel(totalBonusUsed, localization, "I18N.BETSLIP.USED_BONUS")
      : undefined;

  const { message: generosityAlertMessage, icon: generosityIconName } = getGenerosityReceiptAlertData({
    combination,
    localization,
    isFreeBetsWalletsActive,
  });

  const buildSegmentsIcon = () => {
    if (!isOddsboostMarketType) {
      return undefined;
    }

    switch (marketType) {
      case "SUPER_BOOST":
      case "SBG_-_SUPER_BOOST":
        return BetSegmentsIcons.SUPER_BOOST;
      case "SBG_-_DOUBLE_UP_BOOST":
        return BetSegmentsIcons.DOUBLE_UP_BOOST;
      default:
        return BetSegmentsIcons.ODDSBOOST;
    }
  };

  return {
    title,
    subtitle,
    icon,
    meetingCountry,
    trap,
    silkIconAlt: "",
    silkFallbackIconType: silkFallbackType,
    racingSport,
    odds: buildOdds(displayOdds, oddsDisplay),
    previousOdds: previousOdds ? formatOdds(previousOdds, oddsDisplay) : undefined,
    stake: formattedTotalStake,
    profitOrLiability: buildPotentialReturns(totalStake, totalPotentialReturns, localization),
    previousProfitOrLiability,
    segmentsIcon: buildSegmentsIcon(),
    boostedInfo: isOddsboostMarketType ? getBoostedInfo(marketType) : undefined,
    hasBonusUsed: hasBonusUsed && !isFreeBetsWalletsActive,
    freeBetsLabel,
    generosityAlertMessage,
    generosityIconName,
    hasEachWay,
    eachWaySubtitle,
    hasMyOddsBoost: combination.isPriceBoosted,
    isPriceBoosted: !!isOddsboostMarketType,
    isGuaranteedPriceSelected: isGuaranteedPriceSelected && isBestOddsGuaranteedLabelEnabled,
    is90Min: metadata.is90Min,
    selectionTypeIcon: isSelectionTypeIconEnabled
      ? getSelectionTypeIcon(metadata.marketType, metadata.isSuperSub)
      : undefined,
    betReceiptId,
    regulatorId,
    betId: String(combination.betId),
    isPushNotificationsUnavailable,
  };
};

export const createSinglesBuilder = () => {
  return createSelector(
    [
      getSportsbookReport,
      (_state: ApplicationState, { oddsDisplay }: ReceiptBuilderProps) => oddsDisplay,
      (_state: ApplicationState, { localization }: ReceiptBuilderProps) => localization,
      (_state: ApplicationState, { capabilities }: ReceiptBuilderProps) =>
        capabilities.receiptPushNotifications.enabled
          ? capabilities.receiptPushNotifications.unsupportedEventIds
          : NO_UNSUPPORTED_EVENT_IDS,
      (_state: ApplicationState, { capabilities }: ReceiptBuilderProps) =>
        capabilities.receiptPushNotifications.enabled
          ? capabilities.receiptPushNotifications.supportedSportIds
          : NO_SUPPORTED_SPORT_IDS,
      (state: BetslipApplicationState) => !!getHasGenerosityWallets(state),
      (_state: ApplicationState, { capabilities }: ReceiptBuilderProps) => capabilities.selectionTypeIcon.enabled,
      (_state: ApplicationState, { capabilities }: ReceiptBuilderProps) => capabilities.bestOddsGuaranteedLabel.enabled,
    ],
    (
      report,
      oddsDisplay,
      localization,
      unsupportedEventIds,
      supportedSportIds,
      hasGenerosityWallets,
      isSelectionTypeIconEnabled,
      isBestOddsGuaranteedLabelEnabled,
    ) => {
      if (!report) {
        return [];
      }

      const { metadata, result, isFreeBetsSelected } = report;
      const { combinations, legs, relations } = result;
      const placedSingles = Object.values(combinations).filter((combination) => isSingle(combination, legs));

      return placedSingles.map((combination) => {
        const [legId] = combination.legs;
        const { runners: legRunners } = legs[legId];
        const [runnerId] = legRunners;
        const runnerRelations = relations.legsRunners[legId][runnerId] as SportsbookBettingRunnerData;

        return buildSingle(
          combination,
          legs[legId],
          metadata[runnerId],
          runnerRelations,
          oddsDisplay,
          localization,
          unsupportedEventIds,
          supportedSportIds,
          isFreeBetsSelected,
          hasGenerosityWallets,
          isSelectionTypeIconEnabled,
          isBestOddsGuaranteedLabelEnabled,
        );
      });
    },
  );
};

export const buildBetBuilder = (
  entities: Entities,
  combination: PlaceResult.PlacedCombination,
  metadata: RunnersMetadata,
  legs: PlaceResult.PlacedLegsMap,
  oddsDisplay: BetslipOddsDisplay,
  localization: BetslipLocalization,
  unsupportedEventIds: string[],
  supportedSportIds: number[],
  isFreeBetsSelected: boolean,
  isFreeBetsWalletsActive: boolean,
  isSelectionTypeIconEnabled: boolean,
): SportsbookReceiptPanelBetBuilder => {
  const getSportEventByURN = createSportEventByURNSelector();
  const getRaceByURN = createRaceByURNSelector();

  let title = "";
  let isPushNotificationsUnavailable = false;

  const {
    betReceiptId,
    betType,
    displayOdds,
    hasBonusUsed,
    isPriceBoosted,
    legs: combinationLegs,
    originalDisplayOdds,
    originalTotalPotentialReturns,
    regulatorId,
    totalBonusUsed,
    totalPotentialReturns,
    totalStake,
  } = combination;

  const selections: BetSelection[] = Object.values(combinationLegs).map((legURN) => {
    const { id, runners } = legs[legURN];
    const runnerID = runners[0]; // any runner works
    const runner = metadata[runnerID];
    title = runner.type === "GENERIC" ? runner.eventName : runner.racing.venue;
    const { title: selectionTitle, subtitle } = buildSelection(runner, localization);

    const isRacingType = runner.type === "RACING";
    const eventId = isRacingType
      ? getRaceByURN(entities.races, runner.racing.urn)?.raceId
      : getSportEventByURN(entities.sportevents, runner.eventUrn)?.eventId?.toString();

    const isPushNotificationsSupported = supportedSportIds.includes(metadata[runnerID].sportId);

    isPushNotificationsUnavailable =
      (!eventId || unsupportedEventIds.includes(eventId)) && isPushNotificationsSupported;

    return {
      urn: runner.runnerUrn,
      id,
      title: selectionTitle,
      subtitle,
      is90Min: runner.is90Min,
      selectionTypeIcon: isSelectionTypeIconEnabled
        ? getSelectionTypeIcon(runner.marketType, runner.isSuperSub)
        : undefined,
    };
  });

  const key = `I18N.BETSLIP.SBK.MULTIPLE.${betType}_LINES` as const;
  let previousValue: string | undefined;
  let previousOdds: string | undefined;

  if (isPriceBoosted && originalTotalPotentialReturns && originalDisplayOdds) {
    previousValue = buildOriginalPotentialReturns(totalStake, originalTotalPotentialReturns, localization);
    previousOdds = formatOdds(toOdds(originalDisplayOdds), oddsDisplay);
  }

  const freeBetsLabel =
    !isFreeBetsWalletsActive && isFreeBetsSelected
      ? buildSportsbookFreeBetsLabel(totalBonusUsed, localization, "I18N.BETSLIP.USED_BONUS")
      : undefined;

  const { message: generosityAlertMessage, icon: generosityIconName } = getGenerosityReceiptAlertData({
    combination,
    localization,
    isFreeBetsWalletsActive,
  });

  return {
    id: betReceiptId,
    title,
    type: i18n({ key }),
    odds: buildOdds(displayOdds, oddsDisplay),
    previousValue,
    previousOdds,
    returns: buildPotentialReturns(totalStake, totalPotentialReturns, localization),
    hasMyOddsBoost: isPriceBoosted,
    hasBonusUsed: hasBonusUsed && !isFreeBetsWalletsActive,
    freeBetsLabel,
    generosityAlertMessage,
    generosityIconName,
    stake: currencyFormatWithDecimalPlaces({
      ...localization,
      value: totalStake,
    }),
    selectionsLabel: i18n({
      key: "I18N.BETSLIP.SELECTIONS_COUNT",
      interpolationValues: { numberOfSelections: selections.length },
    }),
    selections,
    betReceiptId,
    regulatorId,
    betId: String(combination.betId),
    isPushNotificationsUnavailable,
  };
};

export const createBetBuildersBuilder = () => {
  return createSelector(
    [
      getSportsbookReport,
      (_state: ApplicationState, { oddsDisplay }: ReceiptBuilderProps) => oddsDisplay,
      (_state: ApplicationState, { localization }: ReceiptBuilderProps) => localization,
      (_state: ApplicationState, { capabilities }: ReceiptBuilderProps) =>
        capabilities.receiptPushNotifications.enabled
          ? capabilities.receiptPushNotifications.unsupportedEventIds
          : NO_UNSUPPORTED_EVENT_IDS,
      (state: ApplicationState) => state.entities,
      (_state: ApplicationState, { capabilities }: ReceiptBuilderProps) =>
        capabilities.receiptPushNotifications.enabled
          ? capabilities.receiptPushNotifications.supportedSportIds
          : NO_SUPPORTED_SPORT_IDS,
      (state: BetslipApplicationState) => !!getHasGenerosityWallets(state),
      (_state: ApplicationState, { capabilities }: ReceiptBuilderProps) => capabilities.selectionTypeIcon.enabled,
    ],
    (
      report,
      oddsDisplay,
      localization,
      unsupportedEventIds,
      entities,
      supportedSportIds,
      hasGenerosityWallets,
      isSelectionTypeIconEnabled,
    ) => {
      if (!report) {
        return [];
      }

      const {
        metadata,
        result: { combinations, legs },
        isFreeBetsSelected,
      } = report;

      return Object.values(combinations)
        .filter((combination) => isBetBuilder(combination))
        .map((combination) =>
          buildBetBuilder(
            entities,
            combination,
            metadata,
            legs,
            oddsDisplay,
            localization,
            unsupportedEventIds,
            supportedSportIds,
            isFreeBetsSelected,
            hasGenerosityWallets,
            isSelectionTypeIconEnabled,
          ),
        );
    },
  );
};

export const buildMultiBetBuilder = (
  combination: PlaceResult.PlacedCombination,
  oddsDisplay: BetslipOddsDisplay,
  localization: BetslipLocalization,
  isFreeBetsWalletsActive: boolean,
): SportsbookReceiptPanelMultiBetBuilder => {
  const {
    betReceiptId,
    betType,
    displayOdds,
    hasBonusUsed,
    isPriceBoosted,
    originalDisplayOdds,
    originalTotalPotentialReturns,
    regulatorId,
    totalBonusUsed,
    totalPotentialReturns,
    totalStake,
  } = combination;

  const key = `I18N.BETSLIP.SBK.MULTIPLE.${betType}_LINES` as const;
  let previousValue: string | undefined;
  let previousOdds: string | undefined;

  if (isPriceBoosted && originalTotalPotentialReturns && originalDisplayOdds) {
    previousValue = buildOriginalPotentialReturns(totalStake, originalTotalPotentialReturns, localization);
    previousOdds = formatOdds(toOdds(originalDisplayOdds), oddsDisplay);
  }

  const freeBetsLabel =
    !isFreeBetsWalletsActive && hasBonusUsed
      ? buildSportsbookFreeBetsLabel(totalBonusUsed, localization, "I18N.BETSLIP.USED_BONUS")
      : undefined;

  const { message: generosityAlertMessage, icon: generosityIconName } = getGenerosityReceiptAlertData({
    combination,
    localization,
    isFreeBetsWalletsActive,
  });

  return {
    title: i18n({ key }),
    odds: buildOdds(displayOdds, oddsDisplay),
    previousOdds,
    previousValue,
    returns: buildPotentialReturns(totalStake, totalPotentialReturns, localization),
    stake: currencyFormatWithDecimalPlaces({
      ...localization,
      value: totalStake,
    }),
    hasBonusUsed: hasBonusUsed && !isFreeBetsWalletsActive,
    freeBetsLabel,
    generosityAlertMessage,
    generosityIconName,
    betReceiptId,
    regulatorId,
    betId: String(combination.betId),
    hasMyOddsBoost: isPriceBoosted,
  };
};

export const createMultiBetBuilder = () => {
  return createSelector(
    [
      getSportsbookReport,
      (_state: ApplicationState, { oddsDisplay }: ReceiptBuilderProps) => oddsDisplay,
      (_state: ApplicationState, { localization }: ReceiptBuilderProps) => localization,
      (state: BetslipApplicationState) => !!getHasGenerosityWallets(state),
    ],
    (report, oddsDisplay, localization, hasGenerosityWallets) => {
      if (!report) {
        return undefined;
      }

      const multiBetBuilder = Object.values(report.result.combinations).find((combination) =>
        isMultiBetBuilder(combination),
      );

      if (!multiBetBuilder) {
        return undefined;
      }

      return buildMultiBetBuilder(multiBetBuilder, oddsDisplay, localization, hasGenerosityWallets);
    },
  );
};

const buildMultiBetBuilderGroups = (
  combination: PlaceResult.PlacedCombination,
  report: BetslipSportsbookReport,
  localization: BetslipLocalization,
  isSelectionTypeIconEnabled: boolean,
): SportsbookReceiptPanelProps["multiBetBuilderGroups"] => {
  const {
    result: { legs },
    metadata,
  } = report;

  return combination.legs.reduce((finalEventGroupMap: SportsbookReceiptPanelMultiBetBuilderGroups, legId: string) => {
    const currentLeg = legs[legId];
    const [anyRunnerId] = currentLeg.runners;
    const runnerMetadata = metadata[anyRunnerId];
    const { groupUrn, groupTitle } = buildMultiBetBuilderGroup(runnerMetadata, localization);
    const currentEventGroup = finalEventGroupMap[groupUrn] || {
      urn: groupUrn,
      title: groupTitle,
      selections: [],
    };
    const { title, subtitle, icon, racingSport, meetingCountry, trap, silkFallbackType } = buildSelection(
      runnerMetadata,
      localization,
    );
    const selection = {
      id: legId,
      title,
      subtitle,
      is90Min: runnerMetadata.is90Min,
      selectionTypeIcon: isSelectionTypeIconEnabled
        ? getSelectionTypeIcon(runnerMetadata.marketType, runnerMetadata.isSuperSub)
        : undefined,
      icon,
      racingSport,
      meetingCountry,
      trap,
      silkFallbackType,
    };
    currentEventGroup.selections.push(selection);
    finalEventGroupMap[groupUrn] = currentEventGroup;
    return finalEventGroupMap;
  }, {});
};

export const createMultiBetBuilderGroups = () => {
  return createSelector(
    [
      getSportsbookReport,
      (_state: ApplicationState, { localization }: ReceiptBuilderProps) => localization,
      (_state: ApplicationState, { capabilities }: ReceiptBuilderProps) => capabilities.selectionTypeIcon.enabled,
    ],
    (report, localization, isSelectionTypeIconEnabled): SportsbookReceiptPanelProps["multiBetBuilderGroups"] => {
      if (!report) {
        return undefined;
      }

      const multiBetBuilder = Object.values(report.result.combinations).find((combination) =>
        isMultiBetBuilder(combination),
      );

      if (!multiBetBuilder) {
        return undefined;
      }

      return buildMultiBetBuilderGroups(multiBetBuilder, report, localization, isSelectionTypeIconEnabled);
    },
  );
};

export const createBoostedMultiplesBuilder = () => {
  return createSelector(
    [
      getSportsbookReport,
      (_state: ApplicationState, { oddsDisplay }: ReceiptBuilderProps) => oddsDisplay,
      (_state: ApplicationState, { localization }: ReceiptBuilderProps) => localization,
      (state: BetslipApplicationState) => !!getHasGenerosityWallets(state),
    ],
    (report, oddsDisplay, localization, hasGenerosityWallets) => {
      if (!report) {
        return [];
      }

      const { isFreeBetsSelected } = report;

      return Object.values(report.result.combinations)
        .filter((combination) => isBoostedMultiple(combination))
        .sort((combinationA, combinationB) => combinationA.lines - combinationB.lines)
        .map<SportsbookReceiptPanelProps["boostedMultiples"][0]>((combination) => {
          const combinationVm = buildMultiple(
            combination,
            oddsDisplay,
            localization,
            isFreeBetsSelected,
            hasGenerosityWallets,
          );
          const legs = combination.legs.map((legId) => report.result.legs[legId]);
          const selections = legs.map((leg) => mapBoostedLeg(leg, report, localization, oddsDisplay));

          return {
            ...combinationVm,
            id: combination.id,
            selections,
          };
        });
    },
  );
};
